import {
  Center,
  Heading,
  Input,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { AlertDialog } from 'components/dialogs/AlertDialog';
import { PublishFolderItemDialog } from 'components/Folder/FolderNodeOptionsMenu/PublishFolderItemDialog';
import { OverlayProps } from 'components/TaskOverlay';
import React, { useEffect, useState } from 'react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { useFolderItem } from '../../../ItemViewerProvider';
import { PublishReminderDialog_folderItem$key } from './__generated__/PublishReminderDialog_folderItem.graphql';
import { PublishReminderDialog_renameFolderNode_Mutation } from './__generated__/PublishReminderDialog_renameFolderNode_Mutation.graphql';

const folderItemFragment = graphql`
  fragment PublishReminderDialog_folderItem on FolderItem {
    id
    name
    canRename {
      value
    }
    content {
      __typename
      ... on Page {
        id
      }
      ... on Form {
        id
      }
    }
    ...PublishFolderItemDialog_folderItem
  }
`;

const renameFolderItemMutation = graphql`
  mutation PublishReminderDialog_renameFolderNode_Mutation(
    $folderNodeId: ID!
    $name: String!
  ) {
    updateFolderNode(input: { folderNodeId: $folderNodeId, name: $name }) {
      folderNodeUpdated {
        ... on FolderNodeInterface {
          name
        }
        ... on Node {
          id
        }
      }
    }
  }
`;

interface PublishReminderDialogProps extends OverlayProps {
  folderItem: PublishReminderDialog_folderItem$key;
  persistingChanges: boolean;
}

export const PublishReminderDialog = ({
  folderItem,
  isOpen,
  onClose,
  persistingChanges,
}: PublishReminderDialogProps) => {
  const node = useFragment(folderItemFragment, folderItem);

  const { closeViewer } = useFolderItem();

  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNewName(node.name);
    }
  }, [node.name, isOpen]);

  const {
    isOpen: isConfirmPublishDialogOpen,
    onOpen: openConfirmPublishDialog,
  } = useDisclosure();

  const [renameFolderItem, isLoading] =
    useMutation<PublishReminderDialog_renameFolderNode_Mutation>(
      renameFolderItemMutation,
    );

  let dialogDescription = t`This item is not yet visible to other members, as it is still a draft.`;
  let actionButtonLabel = t`Publish this item`;
  switch (node.content.__typename) {
    case 'Page':
      dialogDescription = t`This page is not yet visible to other members, as it is still a draft.`;
      actionButtonLabel = t`Publish this page`;
      break;
    case 'Form':
      dialogDescription = t`This form is not yet visible to other members, as it is still a draft.`;
      actionButtonLabel = t`Publish this form`;
      break;
  }

  if (persistingChanges) {
    return (
      <AlertDialog
        data-qa="publish-reminder-dialog"
        isOpen={isOpen}
        onClose={onClose}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        title={t`Do you want to publish this?`}
      >
        <Center>
          <Spinner />
        </Center>
      </AlertDialog>
    );
  }

  return (
    <>
      <AlertDialog
        data-qa="publish-reminder-dialog"
        isOpen={isOpen}
        onClose={onClose}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        title={t`Do you want to publish this?`}
        cancelButton={{
          title: t`Keep as draft`,
          onClick: () => {
            if (node.name !== newName) {
              renameFolderItem({
                variables: {
                  folderNodeId: node.id,
                  name: newName,
                },
                onCompleted: () => {
                  closeViewer();
                },
              });
            } else {
              closeViewer();
            }
          },
          isDisabled: isLoading,
          isLoading,
        }}
        actionButton={{
          title: actionButtonLabel,
          onClick: () => {
            if (node.name !== newName) {
              renameFolderItem({
                variables: {
                  folderNodeId: node.id,
                  name: newName,
                },
                onCompleted: () => {
                  openConfirmPublishDialog();
                },
              });
            } else {
              openConfirmPublishDialog();
            }
          },
          isDisabled: isLoading,
          isLoading,
        }}
      >
        <Stack spacing={node.canRename.value ? 1 : 0}>
          <Text>{dialogDescription}</Text>

          {node.canRename.value && (
            <DocumentNameInput setNewName={setNewName} newName={newName} />
          )}
        </Stack>
      </AlertDialog>

      <PublishFolderItemDialog
        folderItem={node}
        isOpen={isConfirmPublishDialogOpen}
        onClose={closeViewer}
      />
    </>
  );
};

interface DocumentNameInputProps {
  newName: string;
  setNewName: (newName: string) => void;
}

const DocumentNameInput: React.FC<DocumentNameInputProps> = ({
  newName,
  setNewName,
}) => {
  return (
    <Stack>
      <Heading fontSize="xs">{t`Name`}</Heading>
      <Input
        value={newName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setNewName(e.target.value)
        }
        // @TODO Without the setTimeout we see `onCLick` a quick select & unselect of the input.
        //       So by delaying it, we allow the select to win
        onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
          setTimeout(() => e.target.select(), 25)
        }
      />
    </Stack>
  );
};
